import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

interface ReportType {
  [key: string]: string;
}

interface ColumnGroup {
  label: string;
  columns: {
    [key: string]: string;
  };
}

interface AvailableColumns {
  [key: string]: ColumnGroup;
}

interface ReportPreset {
  id: number;
  name: string;
  report_type: string;
  start_date: string | null;
  end_date: string | null;
  selected_columns: string[];
  created_at: string;
}

interface ReportSchedule {
  id: number;
  name: string;
  preset_id: number;
  preset_name: string;
  date_range_type: string;
  custom_start_date: string | null;
  custom_end_date: string | null;
  frequency: string;
  time: string;
  days_of_week: number[];
  day_of_month: number | null;
  recipients: Array<{ id: number; email: string; name: string }>;
  is_active: boolean;
  last_run_at: string | null;
  created_at: string;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export function AdminReportsPage() {
  const { user } = useAuth();
  const [reportType, setReportType] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<"reports" | "schedules">("reports");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ReportSchedule | null>(null);
  // Dealer scope for internal users: all dealers vs selected dealers
  const [reportDealerScope, setReportDealerScope] = useState<"all" | "selected">("all");
  const [reportSelectedOrgIds, setReportSelectedOrgIds] = useState<number[]>([]);

  // Fetch report types and available columns
  const { data: reportData } = useQuery({
    queryKey: ["report-types"],
    queryFn: async () => {
      const { data } = await customInstance.get<{
        report_types: ReportType;
        available_columns: AvailableColumns;
      }>("/api/reports/types/");
      return data;
    },
  });

  // Fetch saved presets
  const { data: presets, refetch: refetchPresets } = useQuery({
    queryKey: ["report-presets"],
    queryFn: async () => {
      const { data } = await customInstance.get<ReportPreset[]>("/api/reports/presets/");
      return data;
    },
  });

  // Fetch schedules
  const { data: schedules, refetch: refetchSchedules } = useQuery({
    queryKey: ["report-schedules"],
    queryFn: async () => {
      const { data } = await customInstance.get<ReportSchedule[]>("/api/reports/schedules/");
      return data;
    },
    enabled: activeTab === "schedules",
  });

  // Organizations the user can report on (for dealer selector; internal only)
  const { data: reportOrgs } = useQuery({
    queryKey: ["report-organizations"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ id: number; name: string }[]>("/api/reports/organizations/");
      return Array.isArray(data) ? data : [];
    },
    enabled: activeTab === "reports" && (user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER"),
  });

  // Fetch allowed recipient users for schedule form (own org + account managers + super admins; dealers get empty)
  const { data: users, isLoading: isLoadingUsers, error: usersError } = useQuery({
    queryKey: ["report-recipient-users"],
    queryFn: async () => {
      const { data } = await customInstance.get<User[]>("/api/reports/recipient-users/");
      return Array.isArray(data) ? data : [];
    },
    enabled: activeTab === "schedules",
  });

  // Load preset
  const loadPreset = (preset: ReportPreset) => {
    setReportType(preset.report_type);
    setStartDate(preset.start_date || "");
    setEndDate(preset.end_date || "");
    setSelectedColumns(preset.selected_columns);
    toast.success(`Loaded preset: ${preset.name}`);
  };

  // Save preset
  const savePresetMutation = useMutation({
    mutationFn: async (name: string) => {
      await customInstance.post("/api/reports/presets/save/", {
        name,
        report_type: reportType,
        start_date: startDate,
        end_date: endDate,
        selected_columns: selectedColumns,
      });
    },
    onSuccess: () => {
      refetchPresets();
      setShowPresetModal(false);
      setPresetName("");
      toast.success("Preset saved");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to save preset");
    },
  });

  // Delete preset
  const deletePresetMutation = useMutation({
    mutationFn: async (id: number) => {
      await customInstance.delete(`/api/reports/presets/${id}/`);
    },
    onSuccess: () => {
      refetchPresets();
      toast.success("Preset deleted");
    },
  });

  // Export report
  const exportReport = async () => {
    if (!reportType) {
      toast.error("Please select a report type");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please select a date range");
      return;
    }
    if (selectedColumns.length === 0) {
      toast.error("Please select at least one column");
      return;
    }

    setIsExporting(true);
    try {
      const payload: Record<string, unknown> = {
        report_type: reportType,
        start_date: startDate,
        end_date: endDate,
        selected_columns: selectedColumns,
      };
      if (user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER") {
        if (reportDealerScope === "selected" && reportSelectedOrgIds.length > 0) {
          payload.organization_ids = reportSelectedOrgIds;
        }
      }
      const response = await customInstance.post(
        "/api/reports/export/",
        payload,
        {
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = `report_${new Date().toISOString().split("T")[0]}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report exported successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  // Toggle column selection
  const toggleColumn = (columnKey: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnKey)) {
        return prev.filter((col) => col !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  // Select all columns in a group
  const selectGroupColumns = (groupKey: string) => {
    if (!reportData?.available_columns[groupKey]) return;
    const groupColumns = Object.keys(reportData.available_columns[groupKey].columns);
    setSelectedColumns((prev) => {
      const newCols = [...prev];
      groupColumns.forEach((col) => {
        if (!newCols.includes(col)) {
          newCols.push(col);
        }
      });
      return newCols;
    });
  };

  // Deselect all columns in a group
  const deselectGroupColumns = (groupKey: string) => {
    if (!reportData?.available_columns[groupKey]) return;
    const groupColumns = Object.keys(reportData.available_columns[groupKey].columns);
    setSelectedColumns((prev) => prev.filter((col) => !groupColumns.includes(col)));
  };

  // Ensure internal_id is always selected
  useEffect(() => {
    if (!selectedColumns.includes("internal_id")) {
      setSelectedColumns((prev) => ["internal_id", ...prev]);
    }
  }, [selectedColumns]);

  // Schedule mutations
  const createScheduleMutation = useMutation({
    mutationFn: async (scheduleData: any) => {
      await customInstance.post("/api/reports/schedules/create/", scheduleData);
    },
    onSuccess: () => {
      refetchSchedules();
      setShowScheduleModal(false);
      setEditingSchedule(null);
      toast.success("Schedule created");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to create schedule");
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      await customInstance.put(`/api/reports/schedules/${id}/`, data);
    },
    onSuccess: () => {
      refetchSchedules();
      setShowScheduleModal(false);
      setEditingSchedule(null);
      toast.success("Schedule updated");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to update schedule");
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: async (id: number) => {
      await customInstance.delete(`/api/reports/schedules/${id}/delete/`);
    },
    onSuccess: () => {
      refetchSchedules();
      toast.success("Schedule deleted");
    },
  });

  const testScheduleMutation = useMutation({
    mutationFn: async (id: number) => {
      await customInstance.post(`/api/reports/schedules/${id}/test/`);
    },
    onSuccess: () => {
      toast.success("Test report sent successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to send test report");
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="mt-2 text-sm text-gray-600">Export application data and manage scheduled reports</p>
        </div>
      </div>

      {/* Tabs: Scheduled Reports only for Super Admin and DD Account Manager (dealers = ORG_ADMIN/SALES cannot see) */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("reports")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "reports"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Export Reports
          </button>
          {user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER" ? (
            <button
              onClick={() => setActiveTab("schedules")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "schedules"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Scheduled Reports
            </button>
          ) : null}
        </nav>
      </div>

      {activeTab === "reports" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Report Configuration */}
          <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Report Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Report Type */}
              <div>
                <Label>Report Type</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                >
                  <option value="">Select report type...</option>
                  {reportData?.report_types &&
                    Object.entries(reportData.report_types).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Dealer(s) scope: only for Super Admin and Account Manager */}
              {(user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER") && (
                <div className="space-y-2">
                  <Label>Dealer(s)</Label>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="report-dealer-all"
                        name="report-dealer-scope"
                        checked={reportDealerScope === "all"}
                        onChange={() => {
                          setReportDealerScope("all");
                          setReportSelectedOrgIds([]);
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300"
                      />
                      <label htmlFor="report-dealer-all" className="text-sm font-medium">
                        All dealers
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="report-dealer-selected"
                        name="report-dealer-scope"
                        checked={reportDealerScope === "selected"}
                        onChange={() => setReportDealerScope("selected")}
                        className="w-4 h-4 text-blue-600 border-gray-300"
                      />
                      <label htmlFor="report-dealer-selected" className="text-sm font-medium">
                        Selected dealers
                      </label>
                    </div>
                  </div>
                  {reportDealerScope === "selected" && reportOrgs && reportOrgs.length > 0 && (
                    <div className="mt-2 border rounded-md p-2 max-h-40 overflow-y-auto bg-gray-50">
                      {reportOrgs.map((org) => (
                        <div key={org.id} className="flex items-center gap-2 py-1">
                          <input
                            type="checkbox"
                            id={`report-org-${org.id}`}
                            checked={reportSelectedOrgIds.includes(org.id)}
                            onChange={() => {
                              setReportSelectedOrgIds((prev) =>
                                prev.includes(org.id) ? prev.filter((id) => id !== org.id) : [...prev, org.id]
                              );
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                          />
                          <label htmlFor={`report-org-${org.id}`} className="text-sm cursor-pointer">
                            {org.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Column Selection */}
              <div>
                <Label>Selected Columns ({selectedColumns.length})</Label>
                <div className="mt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowColumnModal(true)}
                    className="w-full"
                  >
                    {selectedColumns.length === 0
                      ? "Select Columns"
                      : `${selectedColumns.length} column${selectedColumns.length !== 1 ? "s" : ""} selected`}
                  </Button>
                </div>
              </div>

              {/* Export Button */}
              <Button
                onClick={exportReport}
                disabled={
                  isExporting ||
                  !reportType ||
                  !startDate ||
                  !endDate ||
                  selectedColumns.length === 0 ||
                  (reportDealerScope === "selected" && reportSelectedOrgIds.length === 0)
                }
                className="w-full"
              >
                {isExporting ? "Exporting..." : "Export CSV"}
              </Button>
            </CardContent>
          </Card>
          </div>

          {/* Saved Presets */}
          <div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Saved Presets</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (reportType && selectedColumns.length > 0) {
                      setShowPresetModal(true);
                    } else {
                      toast.error("Please select a report type and at least one column");
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {presets && presets.length > 0 ? (
                <div className="space-y-2">
                  {presets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center justify-between p-2 border rounded hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-sm">{preset.name}</div>
                        <div className="text-xs text-gray-500">
                          {reportData?.report_types[preset.report_type] || preset.report_type}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => loadPreset(preset)}
                        >
                          Load
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm(`Delete preset "${preset.name}"?`)) {
                              deletePresetMutation.mutate(preset.id);
                            }
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No saved presets. Configure a report and click "Save" to create one.
                </p>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
      )}

      {/* Column Selection Modal */}
      <Dialog open={showColumnModal} onOpenChange={setShowColumnModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Columns to Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Selected Columns Section - Reorderable */}
            {selectedColumns.length > 0 && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <h3 className="font-semibold mb-3">Selected Columns (Reorder with ↑↓)</h3>
                <div className="space-y-2">
                  {selectedColumns.map((colKey, index) => {
                    // Find the column label
                    let colLabel = colKey;
                    if (reportData?.available_columns) {
                      for (const group of Object.values(reportData.available_columns)) {
                        if (colKey in group.columns) {
                          colLabel = group.columns[colKey];
                          break;
                        }
                      }
                    }
                    
                    return (
                      <div
                        key={colKey}
                        className="flex items-center justify-between p-2 bg-white rounded border"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-sm text-gray-500 w-8">#{index + 1}</span>
                          <span className="text-sm font-medium">{colLabel}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              if (index > 0) {
                                const newCols = [...selectedColumns];
                                [newCols[index - 1], newCols[index]] = [newCols[index], newCols[index - 1]];
                                setSelectedColumns(newCols);
                              }
                            }}
                            disabled={index === 0}
                            title="Move up"
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              if (index < selectedColumns.length - 1) {
                                const newCols = [...selectedColumns];
                                [newCols[index], newCols[index + 1]] = [newCols[index + 1], newCols[index]];
                                setSelectedColumns(newCols);
                              }
                            }}
                            disabled={index === selectedColumns.length - 1}
                            title="Move down"
                          >
                            ↓
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => toggleColumn(colKey)}
                            disabled={colKey === "internal_id"}
                            title="Remove"
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Column Groups */}
            {reportData?.available_columns &&
              Object.entries(reportData.available_columns).map(([groupKey, group]) => (
                <div key={groupKey} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">{group.label}</h3>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectGroupColumns(groupKey)}
                      >
                        Select All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deselectGroupColumns(groupKey)}
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(group.columns).map(([colKey, colLabel]) => (
                      <label
                        key={colKey}
                        className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedColumns.includes(colKey)}
                          onChange={() => toggleColumn(colKey)}
                          disabled={colKey === "internal_id"} // Always required
                          className="rounded"
                        />
                        <span className="text-sm">{colLabel}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowColumnModal(false)}>
              Close
            </Button>
            <Button onClick={() => setShowColumnModal(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Preset Modal */}
      <Dialog open={showPresetModal} onOpenChange={setShowPresetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Report Preset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Preset Name</Label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., Monthly Approved Deals"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPresetModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (presetName.trim()) {
                  savePresetMutation.mutate(presetName.trim());
                } else {
                  toast.error("Please enter a preset name");
                }
              }}
              disabled={!presetName.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {activeTab === "schedules" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Scheduled Reports</CardTitle>
                <Button
                  onClick={() => {
                    setEditingSchedule(null);
                    setShowScheduleModal(true);
                  }}
                >
                  Create Schedule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {schedules && schedules.length > 0 ? (
                <div className="space-y-4">
                  {schedules.map((schedule) => (
                    <div
                      key={schedule.id}
                      className="border rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{schedule.name}</h3>
                            <span
                              className={`px-2 py-1 text-xs rounded ${
                                schedule.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {schedule.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <div>
                              <strong>Preset:</strong> {schedule.preset_name}
                            </div>
                            <div>
                              <strong>Frequency:</strong> {schedule.frequency} at {schedule.time}
                            </div>
                            <div>
                              <strong>Date Range:</strong> {schedule.date_range_type}
                            </div>
                            {schedule.days_of_week && schedule.days_of_week.length > 0 && (
                              <div>
                                <strong>Days:</strong>{" "}
                                {schedule.days_of_week
                                  .map((d: number) => {
                                    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                                    return days[d];
                                  })
                                  .join(", ")}
                              </div>
                            )}
                            {schedule.day_of_month && (
                              <div>
                                <strong>Day of Month:</strong> {schedule.day_of_month}
                              </div>
                            )}
                            <div>
                              <strong>Recipients:</strong>{" "}
                              {schedule.recipients.length > 0
                                ? schedule.recipients.map((r) => r.email).join(", ")
                                : "None"}
                            </div>
                            {schedule.last_run_at && (
                              <div>
                                <strong>Last Run:</strong>{" "}
                                {new Date(schedule.last_run_at).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingSchedule(schedule);
                              setShowScheduleModal(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              testScheduleMutation.mutate(schedule.id);
                            }}
                            disabled={testScheduleMutation.isPending}
                          >
                            Test
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (confirm(`Delete schedule "${schedule.name}"?`)) {
                                deleteScheduleMutation.mutate(schedule.id);
                              }
                            }}
                          >
                            ×
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">
                  No scheduled reports. Click "Create Schedule" to get started.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSchedule ? "Edit Schedule" : "Create Schedule"}
            </DialogTitle>
          </DialogHeader>
          <ScheduleForm
            schedule={editingSchedule}
            presets={presets || []}
            users={users || []}
            isLoadingUsers={isLoadingUsers}
            usersError={usersError}
            onSave={(data) => {
              if (editingSchedule) {
                updateScheduleMutation.mutate({ id: editingSchedule.id, ...data });
              } else {
                createScheduleMutation.mutate(data);
              }
            }}
            onCancel={() => {
              setShowScheduleModal(false);
              setEditingSchedule(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Schedule Form Component
function ScheduleForm({
  schedule,
  presets,
  users,
  isLoadingUsers = false,
  usersError = null,
  onSave,
  onCancel,
}: {
  schedule: ReportSchedule | null;
  presets: ReportPreset[];
  users: User[];
  isLoadingUsers?: boolean;
  usersError?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(schedule?.name || "");
  const [presetId, setPresetId] = useState<number | "">(schedule?.preset_id || "");
  const [dateRangeType, setDateRangeType] = useState(
    schedule?.date_range_type || "LAST_7_DAYS"
  );
  const [customStartDate, setCustomStartDate] = useState(
    schedule?.custom_start_date || ""
  );
  const [customEndDate, setCustomEndDate] = useState(
    schedule?.custom_end_date || ""
  );
  const [frequency, setFrequency] = useState(schedule?.frequency || "WEEKLY");
  const [time, setTime] = useState(schedule?.time?.slice(0, 5) || "09:00");
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(
    schedule?.days_of_week || []
  );
  const [dayOfMonth, setDayOfMonth] = useState<string>(
    schedule?.day_of_month?.toString() || ""
  );
  const [selectedRecipients, setSelectedRecipients] = useState<number[]>(
    schedule?.recipients.map((r) => r.id) || []
  );
  const [isActive, setIsActive] = useState(schedule?.is_active ?? true);

  const dateRangeOptions = [
    { value: "TODAY", label: "Today" },
    { value: "YESTERDAY", label: "Yesterday" },
    { value: "LAST_7_DAYS", label: "Last 7 Days" },
    { value: "LAST_14_DAYS", label: "Last 14 Days" },
    { value: "LAST_30_DAYS", label: "Last 30 Days" },
    { value: "LAST_WEEK", label: "Last Week (Mon-Sun)" },
    { value: "THIS_WEEK", label: "This Week (Mon-Sun)" },
    { value: "LAST_MONTH", label: "Last Month" },
    { value: "THIS_MONTH", label: "This Month" },
    { value: "CUSTOM", label: "Custom Date Range" },
  ];

  const frequencyOptions = [
    { value: "DAILY", label: "Daily" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "MONTHLY", label: "Monthly" },
  ];

  const dayOptions = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" },
    { value: 5, label: "Saturday" },
    { value: 6, label: "Sunday" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !presetId) {
      toast.error("Name and preset are required");
      return;
    }

    const data: any = {
      name,
      preset_id: Number(presetId),
      date_range_type: dateRangeType,
      frequency,
      time,
      days_of_week: frequency === "WEEKLY" ? daysOfWeek : [],
      day_of_month: frequency === "MONTHLY" && dayOfMonth ? Number(dayOfMonth) : null,
      recipient_ids: selectedRecipients,
      is_active: isActive,
    };

    if (dateRangeType === "CUSTOM") {
      if (!customStartDate || !customEndDate) {
        toast.error("Custom dates are required");
        return;
      }
      data.custom_start_date = customStartDate;
      data.custom_end_date = customEndDate;
    }

    onSave(data);
  };

  const toggleDay = (day: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleRecipient = (userId: number) => {
    setSelectedRecipients((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      <div>
        <Label>Schedule Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Weekly Approved Deals Report"
          required
        />
      </div>

      <div>
        <Label>Preset *</Label>
        <select
          className="w-full px-3 py-2 border rounded-md"
          value={presetId}
          onChange={(e) => setPresetId(e.target.value ? Number(e.target.value) : "")}
          required
        >
          <option value="">Select a preset...</option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label>Date Range Type *</Label>
        <select
          className="w-full px-3 py-2 border rounded-md"
          value={dateRangeType}
          onChange={(e) => setDateRangeType(e.target.value)}
          required
        >
          {dateRangeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {dateRangeType === "CUSTOM" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Start Date *</Label>
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              required
            />
          </div>
          <div>
            <Label>End Date *</Label>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              required
            />
          </div>
        </div>
      )}

      <div>
        <Label>Frequency *</Label>
        <select
          className="w-full px-3 py-2 border rounded-md"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          required
        >
          {frequencyOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label>Time *</Label>
        <Input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          required
        />
      </div>

      {frequency === "WEEKLY" && (
        <div>
          <Label>Days of Week *</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {dayOptions.map((day) => (
              <label
                key={day.value}
                className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={daysOfWeek.includes(day.value)}
                  onChange={() => toggleDay(day.value)}
                  className="rounded"
                />
                <span className="text-sm">{day.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {frequency === "MONTHLY" && (
        <div>
          <Label>Day of Month (1-31)</Label>
          <Input
            type="number"
            min="1"
            max="31"
            value={dayOfMonth}
            onWheel={(e) => {
              e.currentTarget.blur();
              e.preventDefault();
              e.stopPropagation();
            }}
            onChange={(e) => setDayOfMonth(e.target.value)}
            placeholder="e.g., 1 for 1st of month"
          />
        </div>
      )}

      <div>
        <Label>Recipients</Label>
        <div className="border rounded-md p-3 max-h-48 overflow-y-auto mt-2">
          {isLoadingUsers ? (
            <p className="text-sm text-gray-500">Loading users...</p>
          ) : usersError ? (
            <p className="text-sm text-red-500">Error loading users. Please try again.</p>
          ) : users && users.length > 0 ? (
            <div className="space-y-2">
              {users.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedRecipients.includes(user.id)}
                    onChange={() => toggleRecipient(user.id)}
                    className="rounded"
                  />
                  <span className="text-sm">
                    {user.first_name} {user.last_name} ({user.email})
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No users available</p>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="isActive"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="rounded"
        />
        <Label htmlFor="isActive" className="cursor-pointer">
          Active (schedule will run automatically)
        </Label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save Schedule</Button>
      </DialogFooter>
    </form>
  );
}

